import { Badge } from '@/shared/components/Badge'
import { useState, useEffect, useRef } from 'react'
import { useForm, Controller } from 'react-hook-form'
import SelectBuscable from '@/shared/components/SelectBuscable'
import { Save, Upload, FileText, PencilLine, Trash2, CheckCircle, XCircle, Eye } from 'lucide-react'
import { updateGeneralProfile, updateFiscalProfile, updateBankAccount, getSupplierProfile, getSupplierDocuments, uploadSupplierDocument, getProfileCompletion, type DocumentRequirement, type ProfileCompletion } from '@/features/proveedores/api/supplierProfile.service'
import VigenciaBadge from '@/features/proveedores/components/VigenciaBadge'
import FechaVencimientoModal from '@/features/proveedores/components/FechaVencimientoModal'
import { Notification, useNotification } from '@/shared/components/Notification'
import PlazoDelExpediente from '@/features/proveedores/components/PlazoDelExpediente'
import SupplierProfileSummary from '@/features/proveedores/components/SupplierProfileSummary'
import { MEXICAN_STATES, US_STATES, US_CITIES, SAT_FISCAL_REGIMES, SAT_CFDI_USAGE, MEXICAN_BANKS, SERVICE_TYPES } from '@/constants/supplierConstants'
import { formatDate } from '@/shared/lib/dateUtils'
import { config } from '@/config/env'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'

interface SupplierFormProps {
  onBack: () => void
  onSupplierCreated: () => void
}

type FormStep = 1 | 2 | 3 | 4

const SupplierForm = ({ onBack, onSupplierCreated: _onSupplierCreated }: SupplierFormProps) => {
  const [currentStep, setCurrentStep] = useState<FormStep>(1)
  /**
   * SP-214. El tipo se elige una vez, al registrarse. Después solo Compras puede
   * cambiarlo, así que si el perfil ya trae tipo, aquí queda de solo lectura.
   */
  const [tipoYaDefinido, setTipoYaDefinido] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false)
  const [documents, setDocuments] = useState<DocumentRequirement[]>([])
  const [uploadingDocId, setUploadingDocId] = useState<string | null>(null)
  // Archivo elegido esperando a que el proveedor capture su fecha de vencimiento.
  const [pendienteDeSubir, setPendienteDeSubir] = useState<{
    documentTypeId: string
    file: File
    nombre: string
    /** Codigo del catalogo: decide si el modal sugiere una fecha. */
    codigo?: string
    fechaInicial: string
  } | null>(null)
  const [profileCompletion, setProfileCompletion] = useState<ProfileCompletion | null>(null)
  const [showSummary, setShowSummary] = useState(false)
  const [supplierStatus, setSupplierStatus] = useState<string | null>(null)
  // Días de crédito: los asigna SLYRSA desde el panel. Acá solo se muestran,
  // por eso vive en estado y no en el formulario.
  const [creditDays, setCreditDays] = useState<number | null>(null)
  const [hasDocumentsToFix, setHasDocumentsToFix] = useState(false)
  const [availableColonias, setAvailableColonias] = useState<string[]>([])
  const [serviceTypesFromDB, setServiceTypesFromDB] = useState<{id: string, name: string}[]>([])
  const { notification, showSuccess, showError, hideNotification } = useNotification()
  const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({})

  const [formData, setFormData] = useState({
    razonSocial: '',
    nombreComercial: '',
    tipoServicio: '',
    supplierType: '',
    contactoPrincipal: '',
    correoContacto: '',
    rfc: '',
    tipoProveedor: '',
    regimenFiscal: '',
    usoCFDI: '',
    correoFiscal: '',
    telefono: '',
    pais: '',
    estado: '',
    municipio: '',
    calle: '',
    numero: '',
    colonia: '',
    cp: '',
    clabe: '',
    banco: '',
    nombreCuenta: '',
    numeroCuenta: '',
    customBankName: '',
    bankFile: null as File | null,
    existingBankFileUrl: null as string | null,
    existingBankFileName: null as string | null
  })

  const { register, control, formState: { errors }, watch, setValue, trigger } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: formData
  })

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return (
          <Badge color="verde">Aprobado</Badge>
        )
      case 'rejected':
        return (
          <Badge color="rojo">Rechazado</Badge>
        )
      case 'pending_review':
        return (
          <Badge color="amarillo">Pendiente</Badge>
        )
      default:
        return <span className="text-gray-500 text-sm">N/A</span>
    }
  }

  const handleFileUpload = async (documentTypeId: string, file: File, validUntil?: string) => {
    try {
      setUploadingDocId(documentTypeId)
      await uploadSupplierDocument(documentTypeId, file, validUntil)
      showSuccess('¡Éxito!', 'Documento subido correctamente')

      // Recargar documentos y completitud
      const docs = await getSupplierDocuments()
      setDocuments(docs)

      const completion = await getProfileCompletion()
      setProfileCompletion(completion)
    } catch (errorCrudo: unknown) {
      const error = errorCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error al subir documento:', error)
      showError('Error', error.response?.data?.message || 'No se pudo subir el documento')
    } finally {
      setUploadingDocId(null)
    }
  }

  // La subida no arranca al elegir el archivo: primero se pide la fecha de
  // vencimiento, porque va en la misma petición y el proveedor no tiene forma
  // de agregarla después.
  const handleFileInputChange = (documentTypeId: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const doc = documents.find((d) => d.documentType.id === documentTypeId)
      setPendienteDeSubir({
        documentTypeId,
        file,
        nombre: doc?.documentType.name ?? 'Documento',
        codigo: doc?.documentType.code,
        fechaInicial: (doc?.latestDocument?.validUntil ?? '').slice(0, 10),
      })
    }
    // Limpiar el input para permitir resubir el mismo archivo
    e.target.value = ''
  }

  const confirmarSubida = async (validUntil: string | undefined) => {
    if (!pendienteDeSubir) return
    const { documentTypeId, file } = pendienteDeSubir
    await handleFileUpload(documentTypeId, file, validUntil)
    setPendienteDeSubir(null)
  }

  const handleEditDocument = (documentTypeId: string) => {
    fileInputRefs.current[documentTypeId]?.click()
  }


  const handleSave = async () => {
    setIsSaving(true)
    try {
      // Sincronizar formData con valores actuales de RHF
      // Sincronizar formData preservando bankFile y existingBankFileUrl
      const currentValues = watch()
      setFormData(prev => ({ ...prev, ...currentValues, bankFile: prev.bankFile, existingBankFileUrl: prev.existingBankFileUrl, existingBankFileName: prev.existingBankFileName }))

      switch (currentStep) {
        case 1:
          await saveStep1()
          break
        case 2:
          await saveStep2()
          break
        case 3:
          await saveStep3()
          break
        case 4:
          // Documentos se manejan por separado
          break
      }
      showSuccess('¡Éxito!', 'Datos guardados correctamente')
    } catch (errorCrudo: unknown) {
      const error = errorCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error al guardar:', error)
      showError('Error al guardar', error.response?.data?.message || 'No se pudieron guardar los datos')
    } finally {
      setIsSaving(false)
    }
  }

  // Guardar Pestaña 1: Datos Generales
  const saveStep1 = async () => {
    const currentValues = watch()
    await updateGeneralProfile({
      legalName: currentValues.razonSocial,
      commercialName: currentValues.nombreComercial || undefined,
      serviceTypeId: currentValues.tipoServicio,
      // Se omite vacío: el back lo acepta opcional, y mandar cadena vacía
      // guardaría un tipo de proveedor en blanco.
      ...(currentValues.supplierType && {
        supplierType: currentValues.supplierType,
      }),
      mainContactName: currentValues.contactoPrincipal,
      salesEmail: currentValues.correoContacto,
      accountsPayableEmail: currentValues.correoContacto,
      internalRequesterName: currentValues.contactoPrincipal
    })
  }

  // Guardar Pestaña 2: Información Fiscal (2 llamadas)
  const saveStep2 = async () => {
    const currentValues = watch()
    const isOtro = currentValues.pais === 'OTRO'

    // Llamada 1: Actualizar dirección y vatType en /general
    await updateGeneralProfile({
      legalName: currentValues.razonSocial,
      commercialName: currentValues.nombreComercial || undefined,
      vatType: currentValues.regimenFiscal,
      serviceTypeId: currentValues.tipoServicio,
      country: currentValues.pais,
      state: currentValues.estado,
      city: isOtro ? '' : currentValues.municipio,
      municipality: isOtro ? '' : currentValues.municipio,
      suburb: isOtro ? '' : currentValues.colonia,
      postalCode: currentValues.cp,
      street: currentValues.calle,
      externalNumber: isOtro ? '' : currentValues.numero,
      internalNumber: '',
      mainContactName: currentValues.contactoPrincipal,
      mainContactPhone: currentValues.telefono,
      salesEmail: currentValues.correoContacto,
      accountsPayableEmail: currentValues.correoContacto,
      internalRequesterName: currentValues.contactoPrincipal
    })

    // Llamada 2: Actualizar info fiscal en /fiscal
    await updateFiscalProfile({
      taxRegime: currentValues.regimenFiscal,
      cfdiUse: currentValues.usoCFDI,
      taxEmail: currentValues.correoFiscal,
      taxPhone: currentValues.telefono
    })
  }

  // Guardar Pestaña 3: Información Bancaria
  const saveStep3 = async () => {
    const currentValues = watch()

    // Determinar el nombre del banco (del select o del input custom)
    const bankName = currentValues.banco === 'Otro'
      ? currentValues.customBankName
      : currentValues.banco

    await updateBankAccount({
      accountHolderName: currentValues.nombreCuenta || undefined,
      accountNumber: currentValues.numeroCuenta || undefined,
      bankName: bankName,
      clabe: currentValues.clabe,
      currency: 'MXN', // Default currency as per plan
      file: formData.bankFile
    })
  }

  const validateStep = async (step: number): Promise<boolean> => {
    let fieldsToValidate: string[] = []
    switch (step) {
      case 1:
        fieldsToValidate = ['tipoServicio', 'supplierType', 'contactoPrincipal', 'correoContacto']
        if (formData.tipoProveedor === 'corporate') {
          fieldsToValidate.push('razonSocial')
        }
        break
      case 2: {
        const isOtro = watch('pais') === 'OTRO'
        fieldsToValidate = ['rfc', 'regimenFiscal', 'usoCFDI', 'correoFiscal', 'telefono', 'pais', 'cp', 'estado', 'calle']
        if (!isOtro) {
          fieldsToValidate.push('municipio', 'colonia', 'numero')
        }
        break
      }
      case 3: {
        fieldsToValidate = ['clabe', 'banco', 'nombreCuenta']
        // Validar que la carátula bancaria esté presente (archivo nuevo o existente)
        if (!formData.bankFile && !formData.existingBankFileUrl) {
          showError('Carátula requerida', 'Debes adjuntar la carátula bancaria para continuar.')
          return false
        }
        break
      }
      case 4: {
        // Solo validar que los documentos obligatorios estén subidos
        const requiredDocs = documents.filter(d => d.documentType.isRequired)
        const allRequiredUploaded = requiredDocs.every(d => d.latestDocument !== null)
        return allRequiredUploaded
      }
    }

    if (fieldsToValidate.length > 0) {
      return await trigger(fieldsToValidate as any)
    }
    return true
  }

  const handleNext = async () => {
    // Validar el paso actual antes de avanzar
    const isValid = await validateStep(currentStep)

    if (!isValid) {
      showError('Falta información', 'Por favor completa todos los campos requeridos de la pestaña actual.')
      return
    }

    // En paso 4, validar completitud integral y mostrar resumen
    if (currentStep === 4) {
      // Verificar que los documentos ya se cargaron
      if (isLoadingDocuments || documents.length === 0) {
        showError('Cargando', 'Espera a que se carguen los documentos antes de finalizar.')
        return
      }

      const requiredDocs = documents.filter(d => d.documentType.isRequired)
      const allRequiredUploaded = requiredDocs.every(d => d.latestDocument !== null)
      if (!allRequiredUploaded) {
        showError('Expediente incompleto', 'Debes subir todos los documentos obligatorios antes de enviar')
        return
      }

      // Validar completitud integral del expediente (todos los tabs)
      try {
        const completion = await getProfileCompletion()
        setProfileCompletion(completion)
        if (!completion.isComplete) {
          const missing: string[] = []
          if (!completion.sections.general.completed) missing.push('Datos generales')
          if (!completion.sections.fiscal.completed) missing.push('Información fiscal')
          if (!completion.sections.bankAccount.completed) missing.push('Información bancaria')
          if (!completion.sections.documents.completed) missing.push('Documentos')
          showError('Expediente incompleto', `Faltan secciones por completar: ${missing.join(', ')}`)
          return
        }
      } catch {
        showError('Error', 'No se pudo verificar la completitud del expediente')
        return
      }

      setShowSummary(true)
      return
    }

    // Guardar antes de avanzar en pasos 1-3
    setIsSaving(true)
    try {
      // Sincronizar formData con valores actuales de RHF
      // Preservar bankFile y existingBankFileUrl que no están en RHF
      const currentValues = watch()
      setFormData(prev => ({ ...prev, ...currentValues, bankFile: prev.bankFile, existingBankFileUrl: prev.existingBankFileUrl, existingBankFileName: prev.existingBankFileName }))

      switch (currentStep) {
        case 1:
          await saveStep1()
          break
        case 2:
          await saveStep2()
          break
        case 3:
          await saveStep3()
          break
      }

      setCurrentStep((currentStep + 1) as FormStep)
    } catch (errorCrudo: unknown) {
      const error = errorCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error al guardar:', error)
      showError('Error al guardar', error.response?.data?.message || error.message || 'Por favor, verifica la información e intenta nuevamente')
    } finally {
      setIsSaving(false)
    }
  }

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep((currentStep - 1) as FormStep)
    }
  }

  const steps = [
    { number: 1, label: 'Datos generales' },
    { number: 2, label: 'Información fiscal' },
    { number: 3, label: 'Información bancaria' },
    { number: 4, label: 'Documentos requeridos' }
  ]

  const loadProfileData = async () => {
    try {
      setIsLoading(true)
      const [profile, completion, docs] = await Promise.all([
        getSupplierProfile(),
        getProfileCompletion().catch(() => null),
        getSupplierDocuments().catch(() => [])
      ])

      const currentStatus = profile.general.status || null
      setSupplierStatus(currentStatus)
      if (completion) {
        setProfileCompletion(completion)
      }
      if (docs.length > 0) {
        setDocuments(docs)
        const docsNeedFix = docs.some(d =>
          d.latestDocument && ['rejected', 'observed'].includes(d.latestDocument.status)
        )
        setHasDocumentsToFix(docsNeedFix)
        // Si hay documentos que corregir, ir directo al paso 4
        if (docsNeedFix) {
          setCurrentStep(4)
        }
      }

      // Si el expediente ya está completo en pre-registro, abrir directo resumen.
      const shouldOpenSummaryByDefault =
        ((currentStatus === 'pre_registered' || currentStatus === 'completed' || currentStatus === 'completado') &&
          completion?.isComplete === true)
      setShowSummary(shouldOpenSummaryByDefault)

      setCreditDays(profile.general.creditDays ?? null)
      setTipoYaDefinido(Boolean(profile.general.supplierType))

      // Mapear datos del backend al estado del formulario
      const profileData = {
        razonSocial: profile.general.legalName && profile.general.legalName !== profile.general.rfc ? profile.general.legalName : '',
        nombreComercial: profile.general.commercialName || '',
        tipoServicio: profile.general.serviceTypeId || '',
        supplierType: profile.general.supplierType || '',
        contactoPrincipal: profile.general.mainContactName || '',
        correoContacto: profile.general.salesEmail || '',
        rfc: (profile.general.rfc || '').toUpperCase(),
        tipoProveedor: profile.general.personType || '',
        regimenFiscal: profile.general.taxRegime || profile.fiscal?.taxRegime || '',
        usoCFDI: profile.general.cfdiUse || profile.fiscal?.cfdiUse || '',
        correoFiscal: profile.general.taxEmail || profile.fiscal?.taxEmail || '',
        telefono: profile.general.taxPhone || profile.fiscal?.taxPhone || '',
        pais: profile.general.country || '',
        estado: profile.general.state || '',
        municipio: profile.general.municipality || '',
        calle: profile.general.street || '',
        numero: profile.general.externalNumber || '',
        colonia: profile.general.suburb || '',
        cp: profile.general.postalCode || '',
        clabe: profile.primaryBankAccount?.clabe || '',
        banco: profile.primaryBankAccount?.bankName || '',
        nombreCuenta: profile.primaryBankAccount?.accountHolderName || '',
        numeroCuenta: profile.primaryBankAccount?.accountNumber || '',
        customBankName: '',
        bankFile: null,
        existingBankFileUrl: profile.primaryBankAccount?.coverUrl || null,
        existingBankFileName: profile.primaryBankAccount?.coverOriginalName || null
      }
      setFormData(profileData)

      // Sincronizar con React Hook Form
      Object.entries(profileData).forEach(([key, value]) => {
        setValue(key as any, value)
      })
    } catch {
      showError('Error', 'No se pudo cargar la información del perfil')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const baseUrl = import.meta.env.VITE_API_URL || 'https://api-slyrsa.mimic.agency/api';
        const response = await fetch(`${baseUrl}/catalogos/categorias-proveedores`);

        if (response.ok) {
          const data = await response.json();
          setServiceTypesFromDB(data);
        }
      } catch (error) {
        console.error('Error al cargar las categorías de servicio:', error);
      }
    };

    fetchCategories();
  }, []);

  // Cargar datos del perfil al montar el componente
  useEffect(() => {
    loadProfileData()
  }, [setValue])

  // Efecto para autocompletar dirección con CP
  const watchedCp = watch('cp')
  const watchedPais = watch('pais')

  useEffect(() => {
    const fetchPostalCodeData = async () => {
      // Solo ejecutar si el país es México y el CP tiene 5 dígitos numéricos
      if (watchedPais === 'MX' && watchedCp && watchedCp.length === 5 && /^\d+$/.test(watchedCp)) {
        try {
          const response = await fetch(`${config.apiBaseUrl}/catalogos/postal-codes/${watchedCp}`)

          if (response.ok) {
            const data = await response.json()

            // Autocompletar municipio
            if (data.municipio) {
              setValue('municipio', data.municipio, { shouldValidate: true })
            }

            // Autocompletar estado
            if (data.estado) {
              // Buscar el código del estado basado en el nombre
              const stateObj = MEXICAN_STATES.find(s =>
                s.name.toLowerCase() === data.estado.toLowerCase() ||
                s.name === data.estado
              )
              if (stateObj) {
                setValue('estado', stateObj.code, { shouldValidate: true })
              }
            }

            // Manejar colonias
            if (data.colonias && Array.isArray(data.colonias) && data.colonias.length > 0) {
              setAvailableColonias(data.colonias)

              // Si solo hay una colonia, seleccionarla automáticamente
              if (data.colonias.length === 1) {
                setValue('colonia', data.colonias[0], { shouldValidate: true })
              } else {
                // Si hay varias, verificar si el valor actual es válido
                const currentColonia = watch('colonia')
                if (currentColonia && !data.colonias.includes(currentColonia)) {
                  setValue('colonia', '', { shouldValidate: true })
                }
              }
            } else {
              setAvailableColonias([])
            }
          }
        } catch (error) {
          console.error('Error al obtener datos del código postal:', error)
          setAvailableColonias([])
        }
      } else {
        // En caso de que el usuario cambie el país o borre el CP, resetear colonias
        if (watchedPais !== 'MX' || !watchedCp || watchedCp.length < 5) {
          setAvailableColonias([])
        }
      }
    }

    fetchPostalCodeData()
  }, [watchedCp, watchedPais, setValue])

  // Cargar documentos y completitud cuando se llega a la pestaña 4
  useEffect(() => {
    const loadDocumentsAndCompletion = async () => {
      if (currentStep === 4) {
        try {
          setIsLoadingDocuments(true)
          if (documents.length === 0) {
            setIsLoadingDocuments(true)
            const docs = await getSupplierDocuments()
            setDocuments(docs)
            setIsLoadingDocuments(false)
          }

          const completion = await getProfileCompletion()
          setProfileCompletion(completion)
        } catch {
          setIsLoadingDocuments(false)
          showError('Error', 'No se pudieron cargar los datos')
        } finally {
          setIsLoadingDocuments(false)
        }
      }
    }

    loadDocumentsAndCompletion()
  }, [currentStep])

  // Verificar si el estatus requiere vista de solo lectura.
  // Si el proveedor tiene documentos rechazados u observados, se le permite editar
  // aunque el estatus sea pending_purchases o pending_controlling.
  const isReadOnlyStatus = supplierStatus && [
    'approved',
    'sent_to_review',
    'pending_purchases',
    'pending_controlling'
  ].includes(supplierStatus) && !hasDocumentsToFix

  // Si el estatus es de solo lectura, mostrar vista de resumen
  if (isReadOnlyStatus && !isLoading) {
    return (
      <SupplierProfileSummary
        onBack={onBack}
        onSubmitSuccess={() => { }}
        isApproved={supplierStatus === 'approved'}
        status={supplierStatus}
        showRequestedDocs={true}
        canManageOwnDocs={true}
        plazoDelExpediente={profileCompletion?.profileDeadline}
      />
    )
  }

  // Mostrar vista de resumen si showSummary es true
  if (showSummary) {
    return (
      <SupplierProfileSummary
        onBack={() => setShowSummary(false)}
        onSubmitSuccess={async () => {
          await loadProfileData()
        }}
        // En flujo de corrección el proveedor no necesita re-enviar:
        // el admin ya ve los documentos actualizados. Pasar status oculta el botón de envío.
        status={hasDocumentsToFix ? supplierStatus ?? undefined : undefined}
        canManageOwnDocs={true}
        plazoDelExpediente={profileCompletion?.profileDeadline}
      />
    )
  }

  if (isLoading) {
    return (
      <div className="w-full max-w-none px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="text-gray-600">Cargando información...</p>
        </div>
      </div>
    )
  }

  return (
    <>
      <Notification
        type={notification.type}
        title={notification.title}
        message={notification.message}
        isVisible={notification.isVisible}
        onClose={hideNotification}
      />
      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Completar mi expediente</h1>
          <p className="text-sm text-gray-600 mt-1">Llena el formulario con tus datos para crear tu expediente</p>
        </div>

        <PlazoDelExpediente plazo={profileCompletion?.profileDeadline} vista="proveedor" className="mb-6" />

        {/* Steps Navigation */}
        <div className="flex gap-3 mb-8">
          {steps.map((step) => (
            <button
              key={step.number}
              onClick={async () => {
                if (currentStep === step.number) return

                if (step.number > currentStep) {
                  const isValid = await validateStep(currentStep)
                  if (!isValid) {
                    showError('Falta información', 'Por favor completa todos los campos requeridos de la pestaña actual.')
                    return
                  }
                }

                setCurrentStep(step.number as FormStep)
              }}
              className={`flex-1 px-6 py-3 rounded-lg text-sm font-medium transition-all ${currentStep === step.number
                ? 'text-white shadow-md'
                : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-300'
                }`}
              style={currentStep === step.number ? { backgroundColor: '#315FB9' } : {}}
            >
              {step.number}. {step.label}
            </button>
          ))}
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">
            {steps[currentStep - 1].label}
          </h2>

          {currentStep === 1 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {formData.tipoProveedor === 'corporate'
                    ? 'Razón social *'
                    : formData.tipoProveedor === 'foreign'
                      ? 'Nombre completo / Razón social'
                      : 'Nombre completo'}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    {...register('razonSocial', {
                      required: formData.tipoProveedor === 'corporate' ? 'La razón social es requerida' : false,
                      validate: (value) => {
                        if (!value || !value.trim()) return true
                        return value.trim().length >= 3 || 'Mínimo 3 caracteres'
                      }
                    })}
                    placeholder="Ej: ACME Comercializadora S.A. de C.V."
                    className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm ${errors.razonSocial
                      ? 'border-red-500 bg-red-50 focus:border-red-500'
                      : watch('razonSocial')
                        ? 'border-green-500 bg-green-50 focus:border-green-500'
                        : 'border-gray-300 bg-gray-50 focus:border-blue-500'
                      }`}
                  />
                  {watch('razonSocial') && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {errors.razonSocial ? (
                        <XCircle className="w-5 h-5 text-red-500" />
                      ) : (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      )}
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Captura la razón social tal como aparece en tu constancia fiscal.
                </p>
                {errors.razonSocial && (
                  <p className="text-xs text-red-600 mt-1">{errors.razonSocial.message}</p>
                )}
                {!errors.razonSocial && watch('razonSocial') && (
                  <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre comercial
                </label>
                <div className="relative">
                  <input
                    type="text"
                    {...register('nombreComercial', {
                      minLength: { value: 3, message: 'Mínimo 3 caracteres' }
                    })}
                    placeholder="Ingresa el nombre comercial"
                    className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm ${errors.nombreComercial
                      ? 'border-red-500 bg-red-50 focus:border-red-500'
                      : watch('nombreComercial')
                        ? 'border-green-500 bg-green-50 focus:border-green-500'
                        : 'border-gray-300 bg-gray-50 focus:border-blue-500'
                      }`}
                  />
                  {watch('nombreComercial') && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {errors.nombreComercial ? (
                        <XCircle className="w-5 h-5 text-red-500" />
                      ) : (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      )}
                    </div>
                  )}
                </div>
                {errors.nombreComercial && (
                  <p className="text-xs text-red-600 mt-1">{errors.nombreComercial.message}</p>
                )}
                {!errors.nombreComercial && watch('nombreComercial') && (
                  <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de servicio *
                </label>
                <div className="relative">
                  <Controller name="tipoServicio" control={control} rules={{ required: 'El tipo de servicio es requerido' }} render={({ field }) => (
                    <SelectBuscable variante="formulario" id="tipoServicio" name="tipoServicio" value={field.value ?? ''} onChange={field.onChange} error={Boolean(errors.tipoServicio)} placeholder="Seleccionar tipo de servicio" placeholderDeBusqueda="Buscar tipo de servicio…" opciones={(serviceTypesFromDB.length > 0 ? serviceTypesFromDB : SERVICE_TYPES).map((s) => ({ value: s.id, label: s.name }))} />
                  )} />
                </div>
                {errors.tipoServicio && (
                  <p className="text-xs text-red-600 mt-1">{errors.tipoServicio.message}</p>
                )}
                {!errors.tipoServicio && watch('tipoServicio') && (
                  <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                )}
              </div>

              {/* SP-214: Tipo de proveedor (General / Servicios / Fletes). Define qué
                  documentos/expediente aplican. Distinto del "tipo de persona"
                  (personType) del paso fiscal. */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de proveedor *
                </label>
                <div className="relative">
                  <Controller name="supplierType" control={control} rules={{ required: 'El tipo de proveedor es requerido' }} render={({ field }) => (
                    <SelectBuscable variante="formulario" id="supplierType" name="supplierType" value={field.value ?? ''} onChange={field.onChange} disabled={tipoYaDefinido} error={Boolean(errors.supplierType)} placeholder="Seleccionar" placeholderDeBusqueda="Buscar tipo de proveedor…" opciones={[{ value: 'general', label: 'General' }, { value: 'servicios', label: 'Servicios' }, { value: 'fletes', label: 'Fletes' }]} />
                  )} />
                </div>
                {errors.supplierType && (
                  <p className="text-xs text-red-600 mt-1">{errors.supplierType.message as string}</p>
                )}
                {!errors.supplierType && watch('supplierType') && !tipoYaDefinido && (
                  <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                )}
                {tipoYaDefinido && (
                  <p className="text-xs text-gray-500 mt-1">
                    Para cambiar el tipo de proveedor, solicítalo al área de Compras.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contacto principal *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    {...register('contactoPrincipal', {
                      required: 'El contacto principal es requerido',
                      minLength: { value: 3, message: 'Mínimo 3 caracteres' }
                    })}
                    placeholder="Nombre del contacto"
                    className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm ${errors.contactoPrincipal
                      ? 'border-red-500 bg-red-50 focus:border-red-500'
                      : watch('contactoPrincipal')
                        ? 'border-green-500 bg-green-50 focus:border-green-500'
                        : 'border-gray-300 bg-gray-50 focus:border-blue-500'
                      }`}
                  />
                  {watch('contactoPrincipal') && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {errors.contactoPrincipal ? (
                        <XCircle className="w-5 h-5 text-red-500" />
                      ) : (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      )}
                    </div>
                  )}
                </div>
                {errors.contactoPrincipal && (
                  <p className="text-xs text-red-600 mt-1">{errors.contactoPrincipal.message}</p>
                )}
                {!errors.contactoPrincipal && watch('contactoPrincipal') && (
                  <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Correo de contacto *
                </label>
                <div className="relative">
                  <input
                    type="email"
                    {...register('correoContacto', {
                      required: 'El correo de contacto es requerido',
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: 'Formato de correo inválido'
                      }
                    })}
                    placeholder="Ej: jose@example.com"
                    className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm ${errors.correoContacto
                      ? 'border-red-500 bg-red-50 focus:border-red-500'
                      : watch('correoContacto')
                        ? 'border-green-500 bg-green-50 focus:border-green-500'
                        : 'border-gray-300 bg-gray-50 focus:border-blue-500'
                      }`}
                  />
                  {watch('correoContacto') && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {errors.correoContacto ? (
                        <XCircle className="w-5 h-5 text-red-500" />
                      ) : (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      )}
                    </div>
                  )}
                </div>
                {errors.correoContacto && (
                  <p className="text-xs text-red-600 mt-1">{errors.correoContacto.message}</p>
                )}
                {!errors.correoContacto && watch('correoContacto') && (
                  <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                )}
              </div>

              {/* Días de crédito: los define SLYRSA, el proveedor solo los consulta. */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Días de crédito
                </label>
                <input
                  type="text"
                  value={creditDays != null ? `${creditDays} días` : 'Pendiente por asignar'}
                  readOnly
                  className="w-full px-4 py-2.5 border rounded-lg text-sm border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Este campo no es editable. Lo define SLYRSA y se usa para calcular la
                  fecha de vencimiento de tus facturas.
                </p>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-8">
              {/* Información fiscal */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    RFC *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      {...register('rfc')}
                      readOnly
                      className="w-full px-4 py-2.5 border rounded-lg text-sm uppercase border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Este campo no es editable
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de persona
                  </label>
                  <SelectBuscable
                    variante="formulario"
                    name="personType"
                    value={formData.tipoProveedor ?? ''}
                    onChange={(valor) => setFormData({ ...formData, tipoProveedor: valor })}
                    canDelete={false}
                    placeholder="No especificado"
                    placeholderDeBusqueda="Buscar tipo de persona…"
                    opciones={[
                      { value: 'corporate', label: 'Persona Moral' },
                      { value: 'individual', label: 'Persona Física' },
                      { value: 'foreign', label: 'Extranjero' },
                    ]}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Régimen fiscal *
                  </label>
                  <div className="relative">
                    <Controller name="regimenFiscal" control={control} rules={{ required: 'El régimen fiscal es requerido' }} render={({ field }) => (
                      <SelectBuscable variante="formulario" id="regimenFiscal" name="regimenFiscal" value={field.value ?? ''} onChange={field.onChange} error={Boolean(errors.regimenFiscal)} placeholder="Seleccionar" placeholderDeBusqueda="Buscar régimen…" opciones={SAT_FISCAL_REGIMES.map((r) => ({ value: r.code, label: `${r.code} - ${r.name}` }))} />
                    )} />
                  </div>
                  {errors.regimenFiscal && (
                    <p className="text-xs text-red-600 mt-1">{errors.regimenFiscal.message}</p>
                  )}
                  {!errors.regimenFiscal && watch('regimenFiscal') && (
                    <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Uso de CFDI *
                  </label>
                  <div className="relative">
                    <Controller name="usoCFDI" control={control} rules={{ required: 'El uso de CFDI es requerido' }} render={({ field }) => (
                      <SelectBuscable variante="formulario" id="usoCFDI" name="usoCFDI" value={field.value ?? ''} onChange={field.onChange} error={Boolean(errors.usoCFDI)} placeholder="Seleccionar" placeholderDeBusqueda="Buscar uso de CFDI…" opciones={SAT_CFDI_USAGE.map((u) => ({ value: u.code, label: `${u.code} - ${u.name}` }))} />
                    )} />
                  </div>
                  {errors.usoCFDI && (
                    <p className="text-xs text-red-600 mt-1">{errors.usoCFDI.message}</p>
                  )}
                  {!errors.usoCFDI && watch('usoCFDI') && (
                    <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Correo fiscal *
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      {...register('correoFiscal', {
                        required: 'El correo fiscal es requerido',
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: 'Formato de correo inválido'
                        }
                      })}
                      placeholder="Ej. empresa@example.com"
                      className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm ${errors.correoFiscal
                        ? 'border-red-500 bg-red-50 focus:border-red-500'
                        : watch('correoFiscal')
                          ? 'border-green-500 bg-green-50 focus:border-green-500'
                          : 'border-gray-300 bg-gray-50 focus:border-blue-500'
                        }`}
                    />
                    {watch('correoFiscal') && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {errors.correoFiscal ? (
                          <XCircle className="w-5 h-5 text-red-500" />
                        ) : (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        )}
                      </div>
                    )}
                  </div>
                  {errors.correoFiscal && (
                    <p className="text-xs text-red-600 mt-1">{errors.correoFiscal.message}</p>
                  )}
                  {!errors.correoFiscal && watch('correoFiscal') && (
                    <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Teléfono *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      {...register('telefono', {
                        required: 'El teléfono es requerido',
                        pattern: {
                          value: /^\d{10}$/,
                          message: 'Debe contener exactamente 10 dígitos'
                        }
                      })}
                      placeholder="5512345678"
                      maxLength={10}
                      className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm ${errors.telefono
                        ? 'border-red-500 bg-red-50 focus:border-red-500'
                        : watch('telefono')
                          ? 'border-green-500 bg-green-50 focus:border-green-500'
                          : 'border-gray-300 bg-gray-50 focus:border-blue-500'
                        }`}
                    />
                    {watch('telefono') && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {errors.telefono ? (
                          <XCircle className="w-5 h-5 text-red-500" />
                        ) : (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        )}
                      </div>
                    )}
                  </div>
                  {errors.telefono && (
                    <p className="text-xs text-red-600 mt-1">{errors.telefono.message}</p>
                  )}
                  {!errors.telefono && watch('telefono') && (
                    <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    {watch('telefono')?.length || 0}/10
                  </p>
                </div>
              </div>

              {/* Domicilio fiscal */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Domicilio fiscal</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      País *
                    </label>
                    <div className="relative">
                      <Controller name="pais" control={control} rules={{ required: 'El país es requerido' }} render={({ field }) => (
                        <SelectBuscable variante="formulario" id="pais" name="pais" value={field.value ?? ''} onChange={(valor) => { field.onChange(valor); setValue('estado', ''); setValue('municipio', ''); setValue('colonia', ''); setValue('cp', ''); setValue('numero', ''); setAvailableColonias([]) }} error={Boolean(errors.pais)} placeholder="Seleccionar" placeholderDeBusqueda="Buscar país…" opciones={[{ value: 'MX', label: 'México' }, { value: 'US', label: 'Estados Unidos' }, { value: 'OTRO', label: 'OTRO' }]} />
                      )} />
                    </div>
                    {errors.pais && (
                      <p className="text-xs text-red-600 mt-1">{errors.pais.message}</p>
                    )}
                    {!errors.pais && watch('pais') && (
                      <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                    )}
                  </div>

                  {/* Estado/Provincia */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Estado/Provincia *
                    </label>
                    <div className="relative">
                      {watch('pais') === 'OTRO' ? (
                        <input
                          type="text"
                          {...register('estado', {
                            required: 'El estado/provincia es requerido',
                            minLength: { value: 2, message: 'Mínimo 2 caracteres' }
                          })}
                          placeholder="Ingresa el estado o provincia"
                          className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm ${errors.estado
                            ? 'border-red-500 bg-red-50 focus:border-red-500'
                            : watch('estado')
                              ? 'border-green-500 bg-green-50 focus:border-green-500'
                              : 'border-gray-300 bg-gray-50 focus:border-blue-500'
                            }`}
                        />
                      ) : (
                        <Controller name="estado" control={control} rules={{ required: 'El estado es requerido' }} render={({ field }) => (
                          <SelectBuscable variante="formulario" id="estado" name="estado" value={field.value ?? ''} onChange={(valor) => { field.onChange(valor); setValue('municipio', '') }} error={Boolean(errors.estado)} placeholder="Seleccionar" placeholderDeBusqueda="Buscar estado…" opciones={(watch('pais') === 'MX' ? MEXICAN_STATES : watch('pais') === 'US' ? US_STATES : []).map((e) => ({ value: e.code, label: e.name }))} />
                        )} />
                      )}

                      {watch('estado') && (
                        <div className={`absolute right-3 top-1/2 -translate-y-1/2 ${watch('pais') !== 'OTRO' ? 'pointer-events-none' : ''}`}>
                          {errors.estado ? (
                            <XCircle className="w-5 h-5 text-red-500" />
                          ) : (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          )}
                        </div>
                      )}
                    </div>
                    {errors.estado && (
                      <p className="text-xs text-red-600 mt-1">{errors.estado.message}</p>
                    )}
                    {!errors.estado && watch('estado') && (
                      <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      CP {watch('pais') !== 'OTRO' ? '*' : '(Opcional/ZIP)'}
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        {...register('cp', {
                          required: watch('pais') !== 'OTRO' ? 'El código postal es requerido' : false,
                          pattern: watch('pais') !== 'OTRO' ? {
                            value: /^\d{5}$/,
                            message: 'Debe contener exactamente 5 dígitos'
                          } : undefined
                        })}
                        placeholder={watch('pais') === 'OTRO' ? "Código Postal / ZIP" : "12345"}
                        maxLength={watch('pais') === 'OTRO' ? 10 : 5}
                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm ${errors.cp
                          ? 'border-red-500 bg-red-50 focus:border-red-500'
                          : watch('cp')
                            ? 'border-green-500 bg-green-50 focus:border-green-500'
                            : 'border-gray-300 bg-gray-50 focus:border-blue-500'
                          }`}
                      />
                      {watch('cp') && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          {errors.cp ? (
                            <XCircle className="w-5 h-5 text-red-500" />
                          ) : (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          )}
                        </div>
                      )}
                    </div>
                    {errors.cp && (
                      <p className="text-xs text-red-600 mt-1">{errors.cp.message}</p>
                    )}
                    {!errors.cp && watch('cp') && (
                      <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                    )}
                    {watch('pais') !== 'OTRO' && (
                      <p className="text-xs text-gray-500 mt-1">
                        {watch('cp')?.length || 0}/5
                      </p>
                    )}
                  </div>

                  {watch('pais') !== 'OTRO' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Municipio *
                      </label>
                      <div className="relative">
                        {watch('pais') === 'US' && watch('estado') && US_CITIES[watch('estado')] ? (
                          <div className="relative">
                            <Controller name="municipio" control={control} rules={{ required: 'El municipio es requerido' }} render={({ field }) => (
                              <SelectBuscable variante="formulario" id="municipio" name="municipio" value={field.value ?? ''} onChange={field.onChange} error={Boolean(errors.municipio)} placeholder="Seleccionar municipio/ciudad" placeholderDeBusqueda="Buscar municipio…" opciones={US_CITIES[watch('estado')].map((c) => ({ value: c, label: c }))} />
                            )} />
                          </div>
                        ) : (
                          <input
                            type="text"
                            {...register('municipio', {
                              required: 'El municipio es requerido',
                              minLength: { value: 3, message: 'Mínimo 3 caracteres' }
                            })}
                            placeholder="Ingresa el municipio"
                            className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm ${errors.municipio
                              ? 'border-red-500 bg-red-50 focus:border-red-500'
                              : watch('municipio')
                                ? 'border-green-500 bg-green-50 focus:border-green-500'
                                : 'border-gray-300 bg-gray-50 focus:border-blue-500'
                              }`}
                          />
                        )}

                        {watch('municipio') && (!watch('pais') || watch('pais') !== 'US' || !US_CITIES[watch('estado')] || US_CITIES[watch('estado')].length === 0) && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            {errors.municipio ? (
                              <XCircle className="w-5 h-5 text-red-500" />
                            ) : (
                              <CheckCircle className="w-5 h-5 text-green-500" />
                            )}
                          </div>
                        )}
                      </div>
                      {errors.municipio && (
                        <p className="text-xs text-red-600 mt-1">{errors.municipio.message}</p>
                      )}
                      {!errors.municipio && watch('municipio') && (
                        <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                      )}
                    </div>
                  )}

                  {watch('pais') !== 'OTRO' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Colonia *
                      </label>
                      <div className="relative">
                        {availableColonias.length > 0 ? (
                          <div className="relative">
                            <Controller name="colonia" control={control} rules={{ required: 'La colonia es requerida' }} render={({ field }) => (
                              <SelectBuscable variante="formulario" id="colonia" name="colonia" value={field.value ?? ''} onChange={field.onChange} error={Boolean(errors.colonia)} placeholder="Seleccionar colonia" placeholderDeBusqueda="Buscar colonia…" opciones={availableColonias.map((c) => ({ value: c, label: c }))} />
                            )} />
                          </div>
                        ) : (
                          <input
                            type="text"
                            {...register('colonia', {
                              required: 'La colonia es requerida',
                              minLength: { value: 3, message: 'Mínimo 3 caracteres' }
                            })}
                            placeholder="Ingresa la colonia"
                            className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm ${errors.colonia
                              ? 'border-red-500 bg-red-50 focus:border-red-500'
                              : watch('colonia')
                                ? 'border-green-500 bg-green-50 focus:border-green-500'
                                : 'border-gray-300 bg-gray-50 focus:border-blue-500'
                              }`}
                          />
                        )}
                        {watch('colonia') && availableColonias.length === 0 && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            {errors.colonia ? (
                              <XCircle className="w-5 h-5 text-red-500" />
                            ) : (
                              <CheckCircle className="w-5 h-5 text-green-500" />
                            )}
                          </div>
                        )}
                      </div>
                      {errors.colonia && (
                        <p className="text-xs text-red-600 mt-1">{errors.colonia.message}</p>
                      )}
                      {!errors.colonia && watch('colonia') && (
                        <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {watch('pais') === 'OTRO' ? 'Calle y número *' : 'Calle *'}
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        {...register('calle', {
                          required: 'La calle es requerida',
                          minLength: { value: 3, message: 'Mínimo 3 caracteres' }
                        })}
                        placeholder={watch('pais') === 'OTRO' ? "Ej. Avenida Principal 412" : "Ingresa la calle"}
                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm ${errors.calle
                          ? 'border-red-500 bg-red-50 focus:border-red-500'
                          : watch('calle')
                            ? 'border-green-500 bg-green-50 focus:border-green-500'
                            : 'border-gray-300 bg-gray-50 focus:border-blue-500'
                          }`}
                      />
                      {watch('calle') && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          {errors.calle ? (
                            <XCircle className="w-5 h-5 text-red-500" />
                          ) : (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          )}
                        </div>
                      )}
                    </div>
                    {errors.calle && (
                      <p className="text-xs text-red-600 mt-1">{errors.calle.message}</p>
                    )}
                    {!errors.calle && watch('calle') && (
                      <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                    )}
                  </div>

                  {watch('pais') !== 'OTRO' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Número *
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          {...register('numero', {
                            required: 'El número es requerido'
                          })}
                          placeholder="Ingresa el número"
                          className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm ${errors.numero
                            ? 'border-red-500 bg-red-50 focus:border-red-500'
                            : watch('numero')
                              ? 'border-green-500 bg-green-50 focus:border-green-500'
                              : 'border-gray-300 bg-gray-50 focus:border-blue-500'
                            }`}
                        />
                        {watch('numero') && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            {errors.numero ? (
                              <XCircle className="w-5 h-5 text-red-500" />
                            ) : (
                              <CheckCircle className="w-5 h-5 text-green-500" />
                            )}
                          </div>
                        )}
                      </div>
                      {errors.numero && (
                        <p className="text-xs text-red-600 mt-1">{errors.numero.message}</p>
                      )}
                      {!errors.numero && watch('numero') && (
                        <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                      )}
                    </div>
                  )}

                </div>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" style={{ paddingBottom: '6.25rem' }}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CLABE *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    {...register('clabe', {
                      required: 'La CLABE es requerida',
                      pattern: {
                        value: /^\d{18}$/,
                        message: 'Debe contener exactamente 18 dígitos'
                      }
                    })}
                    placeholder="123456789012345678"
                    maxLength={18}
                    className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm ${errors.clabe
                      ? 'border-red-500 bg-red-50 focus:border-red-500'
                      : watch('clabe')
                        ? 'border-green-500 bg-green-50 focus:border-green-500'
                        : 'border-gray-300 bg-gray-50 focus:border-blue-500'
                      }`}
                  />
                  {watch('clabe') && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {errors.clabe ? (
                        <XCircle className="w-5 h-5 text-red-500" />
                      ) : (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      )}
                    </div>
                  )}
                </div>
                {errors.clabe && (
                  <p className="text-xs text-red-600 mt-1">{errors.clabe.message}</p>
                )}
                {!errors.clabe && watch('clabe') && (
                  <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  {watch('clabe')?.length || 0}/18
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Número de cuenta
                </label>
                <div className="relative">
                  <input
                    type="text"
                    {...register('numeroCuenta')}
                    placeholder="Ej. 1234567890"
                    className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm ${watch('numeroCuenta') ? 'border-green-500 bg-green-50 focus:border-green-500' : 'border-gray-300 bg-gray-50 focus:border-blue-500'}`}
                  />
                  {watch('numeroCuenta') && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Banco *
                </label>
                <div className="relative">
                  <Controller name="banco" control={control} rules={{ required: 'El banco es requerido' }} render={({ field }) => (
                    <SelectBuscable variante="formulario" id="banco" name="banco" value={field.value ?? ''} onChange={field.onChange} error={Boolean(errors.banco)} placeholder="Seleccionar banco" placeholderDeBusqueda="Buscar banco…" opciones={[...MEXICAN_BANKS.map((b) => ({ value: b.name, label: b.name })), { value: 'Otro', label: 'Otro' }]} />
                  )} />
                </div>
                {errors.banco && (
                  <p className="text-xs text-red-600 mt-1">{errors.banco.message}</p>
                )}
                {!errors.banco && watch('banco') && (
                  <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                )}
              </div>

              {watch('banco') === 'Otro' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre del banco *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      {...register('customBankName', {
                        required: watch('banco') === 'Otro' ? 'El nombre del banco es requerido' : false,
                        minLength: { value: 2, message: 'Mínimo 2 caracteres' }
                      })}
                      placeholder="Nombre del Banco"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm"
                    />
                  </div>
                </div>
              )}

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre de cuenta *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    {...register('nombreCuenta', {
                      required: 'El nombre de cuenta es requerido',
                      minLength: { value: 3, message: 'Mínimo 3 caracteres' }
                    })}
                    placeholder="Nombre del Titular de la cuenta"
                    className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm ${errors.nombreCuenta
                      ? 'border-red-500 bg-red-50 focus:border-red-500'
                      : watch('nombreCuenta')
                        ? 'border-green-500 bg-green-50 focus:border-green-500'
                        : 'border-gray-300 bg-gray-50 focus:border-blue-500'
                      }`}
                  />
                  {watch('nombreCuenta') && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {errors.nombreCuenta ? (
                        <XCircle className="w-5 h-5 text-red-500" />
                      ) : (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      )}
                    </div>
                  )}
                </div>
                {errors.nombreCuenta && (
                  <p className="text-xs text-red-600 mt-1">{errors.nombreCuenta.message}</p>
                )}
                {!errors.nombreCuenta && watch('nombreCuenta') && (
                  <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                )}
              </div>

              {/* File Upload for Carátula */}
              <div className="md:col-span-2">
                {/* Si hay archivo nuevo seleccionado o archivo existente, mostrar preview */}
                {(formData.bankFile || formData.existingBankFileUrl) ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Carátula bancaria
                    </label>
                    <div className="inline-flex flex-col items-start">
                      <div className="relative group">
                        {/* Thumbnail */}
                        <a
                          href={formData.bankFile ? URL.createObjectURL(formData.bankFile) : (formData.existingBankFileUrl || '#')}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                        >
                          {(() => {
                            const url = formData.existingBankFileUrl || ''
                            const isImage = formData.bankFile
                              ? formData.bankFile.type.startsWith('image/')
                              : /\.(jpg|jpeg|png)$/i.test(url)

                            if (isImage) {
                              return (
                                <img
                                  src={formData.bankFile ? URL.createObjectURL(formData.bankFile) : url}
                                  alt="Carátula bancaria"
                                  className="w-20 h-24 object-cover rounded-lg border border-gray-200 bg-gray-50"
                                />
                              )
                            }
                            return (
                              <div className="w-20 h-24 rounded-lg border border-gray-200 bg-gray-50 flex flex-col items-center justify-center">
                                <FileText className="w-8 h-8 text-gray-400" />
                                <span className="text-[10px] text-gray-400 mt-1">PDF</span>
                              </div>
                            )
                          })()}
                        </a>
                        {/* Botón eliminar */}
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, bankFile: null, existingBankFileUrl: null, existingBankFileName: null }))}
                          className="absolute -top-2 -right-2 w-5 h-5 bg-red-100 hover:bg-red-200 text-red-500 rounded-full flex items-center justify-center transition-colors"
                          title="Eliminar archivo"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                      {/* Nombre del archivo */}
                      <span className="text-xs text-gray-600 mt-1.5 max-w-[120px] truncate">
                        {formData.bankFile
                          ? formData.bankFile.name
                          : formData.existingBankFileName || (() => {
                            try {
                              const url = formData.existingBankFileUrl || ''
                              const fileName = decodeURIComponent(url.split('/').pop()?.split('?')[0] || '')
                              return fileName || 'Carátula'
                            } catch { return 'Carátula' }
                          })()
                        }
                      </span>
                    </div>
                    {/* Opción de cambiar archivo */}
                    <label className="inline-flex items-center gap-1.5 mt-3 text-xs text-blue-600 hover:text-blue-800 cursor-pointer">
                      <Upload className="w-3.5 h-3.5" />
                      Cambiar archivo
                      <input
                        type="file"
                        className="hidden"
                        accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (file) {
                            setFormData(prev => ({ ...prev, bankFile: file }))
                          }
                        }}
                      />
                    </label>
                  </div>
                ) : (
                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-white">
                    <span className="text-sm font-medium text-gray-700">
                      Adjuntar carátula <span className="text-gray-500 font-normal">(PDF, PNG, JPG)</span>
                    </span>
                    <label className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg cursor-pointer transition-colors text-sm font-medium">
                      <Upload className="w-4 h-4" />
                      Cargar
                      <input
                        type="file"
                        className="hidden"
                        accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (file) {
                            setFormData(prev => ({ ...prev, bankFile: file }))
                          }
                        }}
                      />
                    </label>
                  </div>
                )}
              </div>
            </div>
          )}

          {currentStep === 4 && (
            <div>
              <div className="overflow-x-auto">
                {isLoadingDocuments ? (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p>Cargando documentos...</p>
                  </div>
                ) : documents.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p>No hay tipos de documento configurados para tu categoría.</p>
                    <p className="text-sm mt-1">Contacta al administrador para resolver este problema.</p>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
                  <table className="min-w-full divide-y divide-[#E5E7EB]">
                    <thead className="bg-[#FAF9FF]">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Documento</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Requerido</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Archivo</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Fecha subida</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Fecha vencimiento</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Estatus</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Vigencia</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Comentarios</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-[#E1E1E1]">
                      {documents.map((doc) => (
                        <tr key={doc.documentType.id} className="hover:bg-[#F4F7FB] transition-colors">
                          <td className="px-6 py-4">
                            {/* w-min: el ancho lo define el nombre (que va en una sola
                                linea) y la descripcion hace saltos dentro de ese ancho. */}
                            <div className="w-min">
                              <p className="text-sm font-medium text-gray-900 whitespace-nowrap">{doc.documentType.name}</p>
                              {doc.documentType.description && (
                                <p className="text-xs text-gray-500">{doc.documentType.description}</p>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm">
                            {doc.documentType.isRequired ? (
                              <span className="text-red-600 font-medium">Sí</span>
                            ) : (
                              <span className="text-gray-500">No</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            {doc.latestDocument ? (
                              <a
                                href={doc.latestDocument.fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 underline"
                              >
                                <FileText className="w-4 h-4" />
                                {doc.latestDocument.fileName}
                              </a>
                            ) : (
                              <label className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 underline cursor-pointer">
                                {uploadingDocId === doc.documentType.id ? (
                                  <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                    Subiendo...
                                  </>
                                ) : (
                                  <>
                                    <Upload className="w-4 h-4" />
                                    Subir archivo
                                  </>
                                )}
                                <input
                                  type="file"
                                  className="hidden"
                                  accept={doc.documentType.allowedMimeTypes.join(',')}
                                  onChange={handleFileInputChange(doc.documentType.id)}
                                  disabled={uploadingDocId === doc.documentType.id}
                                />
                              </label>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700">
                            {doc.latestDocument ? formatDate(doc.latestDocument.uploadedAt) : 'N/A'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700">
                            {doc.latestDocument?.validUntil ? formatDate(doc.latestDocument.validUntil) : 'N/A'}
                          </td>
                          <td className="px-6 py-4">
                            {doc.latestDocument ? getStatusBadge(doc.latestDocument.status) : <span className="text-gray-500 text-sm">Sin subir</span>}
                          </td>
                          <td className="px-6 py-4">
                            <VigenciaBadge
                              estado={doc.latestDocument?.estadoVigencia}
                              validUntil={doc.latestDocument?.validUntil}
                            />
                          </td>
                          <td className="px-6 py-4">
                            {doc.latestDocument?.reviewComment ? (
                              <button
                                type="button"
                                onClick={() => showError('Comentario del revisor', doc.latestDocument!.reviewComment || '')}
                                className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium"
                              >
                                <Eye className="w-4 h-4" />
                                Ver
                              </button>
                            ) : (
                              <span className="text-sm text-gray-400">N/A</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              {doc.latestDocument && doc.latestDocument.status !== 'approved' ? (
                                <>
                                  <button
                                    onClick={() => handleEditDocument(doc.documentType.id)}
                                    disabled={uploadingDocId === doc.documentType.id}
                                    className="p-1.5 rounded hover:bg-[#1FB7EE]/10 text-[#1FB7EE] disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Resubir documento"
                                  >
                                    <PencilLine className="w-4 h-4" />
                                  </button>
                                  <input
                                    ref={(el) => { fileInputRefs.current[doc.documentType.id] = el; }}
                                    type="file"
                                    className="hidden"
                                    accept={doc.documentType.allowedMimeTypes.join(',')}
                                    onChange={handleFileInputChange(doc.documentType.id)}
                                    disabled={uploadingDocId === doc.documentType.id}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => showError('No disponible', 'Eliminar documento estará disponible cuando exista el endpoint en el backend.')}
                                    className="p-1.5 rounded hover:bg-[#EF4444]/10 text-[#EF4444]"
                                    title="Eliminar archivo"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </>
                              ) : (
                                <button
                                  disabled={doc.latestDocument?.status === 'approved'}
                                  className="p-1.5 rounded bg-gray-100 text-gray-400 cursor-not-allowed"
                                  title="No se puede editar un documento aprobado"
                                >
                                  <PencilLine className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className={estiloDeBoton('primario')}
          >
            <Save className="w-4 h-4" />
            <span>{isSaving ? 'Guardando...' : 'Guardar'}</span>
          </button>

          <div className="flex items-center gap-3">
            {currentStep > 1 && (
              <button
                onClick={handlePrevious}
                disabled={isSaving}
                className={estiloDeBoton('secundario')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span>Anterior</span>
              </button>
            )}

            <button
              onClick={handleNext}
              disabled={
                isSaving ||
                isLoadingDocuments ||
                (currentStep === 4 && !documents.filter(d => d.documentType.isRequired).every(d => d.latestDocument !== null))
              }
              className={estiloDeBoton('primario')}
            >
              <span>{isSaving ? 'Guardando...' : (currentStep === 4 ? 'Finalizar' : 'Siguiente')}</span>
              {!isSaving && (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div >

      <FechaVencimientoModal
        abierto={!!pendienteDeSubir}
        nombreDocumento={pendienteDeSubir?.nombre ?? ''}
        codigoDocumento={pendienteDeSubir?.codigo}
        fechaInicial={pendienteDeSubir?.fechaInicial}
        subiendo={!!uploadingDocId}
        onCancelar={() => setPendienteDeSubir(null)}
        onConfirmar={confirmarSubida}
      />
    </>
  )
}

export default SupplierForm
